window.cloStickyChatClearance = {
    bars: new Set(),
    connect(bar) {
        if (this.bars.has(bar)) return;
        this.bars.add(bar);
        if (!this.discovery) {
            this.onChange = () => this.schedule();
            this.discovery = new MutationObserver((records) => {
                if ((this.chat && !this.chat.isConnected) || records.some((record) =>
                    Array.from(record.addedNodes).some((node) => node.nodeType === 1 &&
                        (node.matches('shopify-chat') || node.querySelector('shopify-chat'))))) this.findChat();
            });
            this.discovery.observe(document.body, { childList: true, subtree: true });
        }
        this.findChat();
        this.observeBar(bar);
        this.schedule();
    },
    disconnect(bar) {
        this.bars.delete(bar);
        bar.removeEventListener('transitionend', this.onChange);
        this.resizeObserver?.unobserve(bar);
        if (this.bars.size) {
            this.barObserver?.disconnect();
            this.observeBody();
            this.bars.forEach((item) => this.observeBar(item));
            this.schedule();
            return;
        }
        this.releaseChat();
        this.discovery?.disconnect();
        this.discovery = null;
    },
    findChat() {
        const chat = document.querySelector('shopify-chat');
        if (this.chat === chat) return;
        this.releaseChat();
        if (!chat) return;
        this.chat = chat;
        this.chatObserver = new MutationObserver(this.onChange);
        this.chatObserver.observe(chat, { attributes: true, attributeFilter: ['open', 'mode'] });
        this.barObserver = new MutationObserver((records) => {
            if (records.some((record) => record.target === document.body
                ? ['show-mobile-options', 'cart-sidebar-show', 'auth-sidebar-show', 'menu_open', 'open_search_mobile', 'open-mobile-sidebar'].some((state) =>
                    (record.oldValue || '').split(/\s+/).includes(state) !== document.body.classList.contains(state))
                : record.oldValue !== record.target.getAttribute(record.attributeName))) this.schedule();
        });
        this.observeBody();
        if (window.ResizeObserver) this.resizeObserver = new ResizeObserver(this.onChange);
        this.bars.forEach((bar) => this.observeBar(bar));
        window.addEventListener('resize', this.onChange);
        window.visualViewport?.addEventListener('resize', this.onChange);
        customElements.whenDefined('shopify-chat').then(() => {
            if (this.chat === chat) this.schedule();
        });
        this.schedule();
    },
    observeBody() {
        this.barObserver?.observe(document.body, { attributes: true, attributeFilter: ['class'], attributeOldValue: true });
    },
    observeBar(bar) {
        if (!this.chat) return;
        this.barObserver.observe(bar, { attributes: true, attributeFilter: ['class', 'style'], attributeOldValue: true });
        this.resizeObserver?.observe(bar);
        bar.addEventListener('transitionend', this.onChange);
    },
    schedule() {
        if (!this.chat || this.frame) return;
        this.frame = requestAnimationFrame(() => {
            this.frame = 0;
            this.update();
        });
    },
    reset() {
        this.chat?.removeAttribute('data-clo-sticky-clearance');
        this.chat?.style.removeProperty('--clo-sticky-chat-offset');
    },
    update() {
        const chat = this.chat;
        const activeBars = Array.from(this.bars).filter((bar) => {
            if (!bar.isConnected) return false;
            if (bar.classList.contains('halo-sticky-toolbar-mobile')) return window.innerWidth <= 1024;
            return bar.classList.contains('style-1') && !bar.classList.contains('hidden-sticky') &&
                (bar.classList.contains('show-sticky') || (window.innerWidth <= 767 && bar.classList.contains('always-on-mobile')));
        });
        this.reset();
        if (!activeBars.length || !chat?.isConnected || chat.hasAttribute('open') ||
            chat.getAttribute('mode') === 'managed' ||
            document.body.matches('.cart-sidebar-show, .auth-sidebar-show, .menu_open, .open_search_mobile, .open-mobile-sidebar')) return;
        const root = chat.shadowRoot;
        const wrapper = root?.getElementById('shopify-element-wrapper');
        if (root !== this.chatRoot || wrapper !== this.wrapper) {
            this.shadowObserver?.disconnect();
            this.chatRoot = root;
            this.wrapper = wrapper;
            this.shadowObserver = new MutationObserver(this.onChange);
            if (root) this.shadowObserver.observe(root, { childList: true });
            if (wrapper) this.shadowObserver.observe(wrapper, { childList: true });
        }
        const runtime = wrapper?.querySelector('shopify-agent-runtime');
        const launcher = (runtime?.shadowRoot || root)?.querySelector('button.activator[part~="activator"]');
        if (launcher !== this.launcher) {
            if (this.launcher) this.resizeObserver?.unobserve(this.launcher);
            this.launcher = launcher;
            if (launcher) this.resizeObserver?.observe(launcher);
        }
        if (!launcher || !launcher.getClientRects().length) return;
        const nativeOffset = Number.parseFloat(window.getComputedStyle(chat).getPropertyValue('--shopify-chat-activator-offset'));
        if (!Number.isFinite(nativeOffset) || nativeOffset < 0) return;
        const launcherStyle = window.getComputedStyle(launcher);
        if (launcherStyle.display === 'none' || launcherStyle.visibility === 'hidden') return;
        const button = launcher.getBoundingClientRect();
        const obstacles = [];
        activeBars.forEach((bar) => {
            const style = window.getComputedStyle(bar);
            if (style.display === 'none' || style.visibility === 'hidden') return;
            const bounds = bar.getBoundingClientRect();
            if (bounds.height > 0 && bounds.width > 0) obstacles.push(bounds);
        });
        let lift = 0;
        // Work upward so clearing a toolbar cannot place chat inside a higher sticky bar.
        obstacles.sort((a, b) => b.top - a.top).forEach((bounds) => {
            if (button.right > bounds.left && button.left < bounds.right &&
                button.bottom - lift > bounds.top && button.top - lift < bounds.bottom) {
                lift = Math.max(lift, button.bottom - bounds.top + nativeOffset);
            }
        });
        if (lift <= 0 || lift > button.top) return;
        chat.style.setProperty('--clo-sticky-chat-offset', `${nativeOffset + lift}px`);
        chat.setAttribute('data-clo-sticky-clearance', '');
    },
    releaseChat() {
        this.reset();
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = 0;
        this.chatObserver?.disconnect();
        this.barObserver?.disconnect();
        this.resizeObserver?.disconnect();
        this.shadowObserver?.disconnect();
        this.bars.forEach((bar) => bar.removeEventListener('transitionend', this.onChange));
        window.removeEventListener('resize', this.onChange);
        window.visualViewport?.removeEventListener('resize', this.onChange);
        this.chat = this.chatRoot = this.wrapper = this.launcher = null;
        this.chatObserver = this.barObserver = this.resizeObserver = this.shadowObserver = null;
    }
};

class CloChatToolbar extends HTMLElement {
    connectedCallback() {
        window.cloStickyChatClearance.connect(this);
    }

    disconnectedCallback() {
        window.cloStickyChatClearance.disconnect(this);
    }
}

customElements.define('clo-chat-toolbar', CloChatToolbar);
